import { format } from "date-fns";
import React, { useState } from "react";
import DOMPurify from "dompurify";
import { useOutletContext, useParams } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, FormLabel, IconButton, Popover, Tooltip } from "@mui/material";
import { useUserContext } from "../../context/userContext";
import { addReview, deleteReview, editReview } from "../../services/api/bidderService";
import { toast } from "react-toastify";
import RadioGroupRating from "../../components/ui/RadioGroupRating";
import { Editor } from "@tinymce/tinymce-react";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SentimentIconRating from "../../components/ui/SentimentIconRating";

export default function Reviews() {
  const { user } = useUserContext();
  const { bidder, handleUpdateProcurement, procurement } = useOutletContext();
  const { uuidEnterprise, uuidProcurement } = useParams();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    rating: "",
  });

  const [openDeleteDial, setOpenDeleteDial] = useState(false);
  const [openAddEditDial, setOpenAddEditDial] = useState(false);
  const [selectedReview, setSelectedReview] = useState();
  const [reviewBody, setReviewBody] = useState();
  const [rating, setRating] = useState();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleActions = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseActions = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const review = bidder.reviews.find(review => review.uuidProcurement === uuidProcurement);
  const avgRating = bidder.reviews.length > 0 ? Math.round(bidder.reviews.reduce((a, b) => a + b.rating.ratingValue, 0) / bidder.reviews.length) : 5;

  const handleClickOpenDelete = (review) => {
    setSelectedReview(review);
    setOpenDeleteDial(true);
  };

  const handleClickOpenEdit = (review) => {
    setSelectedReview(review);
    setRating(review.rating.ratingValue);
    setReviewBody(review.reviewBody);
    setOpenAddEditDial(true);
  };

  const handleClickOpenAdd = () => {
    setSelectedReview(null);
    setOpenAddEditDial(true);
  };

  const handleCloseDialogs = () => {
    setOpenDeleteDial(false);
    setOpenAddEditDial(false);
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await deleteReview(uuidEnterprise, selectedReview.uuid);
      await handleUpdateProcurement();
      setSelectedReview(null);
    } catch (error) {
      toast.error(error.message);
    } finally {
      handleCloseDialogs();
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (rating) {
      try {
        setLoading(true);
        await addReview(uuidEnterprise, { reviewBody: reviewBody, ratingValue: rating, uuidProcurement: uuidProcurement });
        await handleUpdateProcurement();
        setReviewBody(null);
        setRating(null);
      } catch (error) {
        toast.error(error.message);
      } finally {
        handleCloseDialogs();
        setErrors({
          ...errors,
          rating: "",
        });
        setLoading(false);
      }
    } else {
      setErrors({
        ...errors,
        rating: "Appréciation requise.",
      });
    }
  };

  const handleEdit = async () => {
    if (rating) {
      try {
        setLoading(true);
        await editReview(uuidEnterprise, selectedReview.uuid, { reviewBody: reviewBody, ratingValue: rating });
        await handleUpdateProcurement();
        setSelectedReview(null);
        setReviewBody(null);
        setRating(null);
      } catch (error) {
        toast.error(error.message);
      } finally {
        handleCloseDialogs();
        setErrors({
          ...errors,
          rating: "",
        });
        setLoading(false);
      }
    } else {
      setErrors({
        ...errors,
        rating: "Appréciation requise.",
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">
          <span>Note de l'initiateur </span>
          <Tooltip
            title="Appréciation générale du soumissionnaire"
          >
            <SentimentIconRating rating={avgRating} />
          </Tooltip>
        </h2>
        {user.uuid === procurement.initiator.uuid && !review && (
          <Tooltip title="Ajouter une note" placement="top">
            <IconButton onClick={handleClickOpenAdd}>
              <AddIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      {!review
        ? (
          <p className="text-gray-500 italic">Aucun avis pour cette entreprise.</p>
        )
        : (
          <Card className="max-w-2xl">
            <CardHeader
              title={review.author.fullname}
              subheader={`Publiée le ${format(review.publishedAt, 'dd/MM/yyyy')}`}
              action={(
                <>
                  <SentimentIconRating rating={review.rating.ratingValue} />
                  {user.uuid === review.author.uuid && (
                    <>
                      <IconButton onClick={handleActions}>
                        <MoreVertIcon />
                      </IconButton>
                      <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleCloseActions}
                        elevation={1}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'center',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'center',
                        }}
                      >
                        <div className="flex flex-col gap-2 p-2">
                          <IconButton onClick={() => handleClickOpenEdit(review)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleClickOpenDelete(review)}>
                            <DeleteIcon />
                          </IconButton>
                        </div>
                      </Popover>
                    </>
                  )}
                </>
              )}
            />
            <CardContent>
              <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(review.reviewBody) }} />
            </CardContent>
          </Card>
        )}

      <Dialog
        open={openDeleteDial}
        onClose={handleCloseDialogs}
      >
        <DialogTitle>
          Confirmation de suppression
        </DialogTitle>
        <Divider />
        <DialogContent>
          <DialogContentText>
            Voulez-vous supprimer cette note ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} disabled={loading}>Annuler</Button>
          <Button onClick={handleDelete} loading={loading} autoFocus>
            Valider
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAddEditDial}
        onClose={handleCloseDialogs}
        fullWidth
      >
        <DialogTitle>
          {selectedReview ? "Édition de la note" : "Ajout d'une note"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <FormLabel required>Appréciation</FormLabel>
              <RadioGroupRating defaultValue={selectedReview && selectedReview.rating.ratingValue} onChange={event => setRating(event.target.value)} />
              <span className="text-red-500 text-sm">{errors.rating}</span>
            </div>
            <div className="flex flex-col gap-2">
              <FormLabel>Commentaire</FormLabel>
              <Editor
                apiKey={process.env.REACT_APP_EDITOR}
                onEditorChange={value => setReviewBody(value)}
                initialValue={selectedReview && selectedReview.reviewBody}
                init={{
                  menubar: false,
                  plugins: "lists",
                  toolbar: "bold italic underline bullist numlist",
                  toolbar_mode: "sliding",
                  paste_webkit_styles: "",
                  width: "100%",
                  height: 300,
                  language: "fr_FR",
                  statusbar: true,
                  branding: false,
                  elementpath: false,
                  resize: true,
                }}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} disabled={loading}>Annuler</Button>
          <Button onClick={selectedReview ? handleEdit : handleAdd} loading={loading} autoFocus>
            {selectedReview ? "Modifier" : "Ajouter"}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}
